import React from 'react';
import { Link } from 'react-router-dom';
import { FAQ, FAQSection } from '@/types/faq.types';
import { Assistant } from '@/types';
import { renderAssistantIcon } from '@/utils/RenderAssistantIcon';
import { getIconColorFromAssistant } from '@/utils/colorUtils';

interface FAQContentRendererProps {
    faq: FAQ;
    onSendMessage?: (message: string) => void;
    assistant?: Assistant | null;
}

const renderSection = (section: FAQSection, index: number): React.ReactNode => {
    const headingClasses = section.headingLevel === 'h2'
        ? 'text-base sm:text-xl font-bold text-white mt-4 sm:mt-6 mb-2 sm:mb-3'
        : 'text-sm sm:text-lg font-semibold text-gray-200 mt-3 sm:mt-4 mb-1.5 sm:mb-2';

    return (
        <div key={index} className="mb-4">
            {section.headingLevel === 'h2' ? (
                <h2 className={headingClasses}>{section.heading}</h2>
            ) : (
                <h3 className={headingClasses}>{section.heading}</h3>
            )}

            {section.content && (
                <p className="text-sm sm:text-base text-gray-300 whitespace-pre-line mb-2 sm:mb-3">
                    {section.content}
                </p>
            )}

            {section.listItems && section.listItems.length > 0 && (
                <ul className="list-disc list-inside space-y-1.5 sm:space-y-2 text-sm sm:text-base text-gray-300 ml-2">
                    {section.listItems.map((item, itemIndex) => (
                        <li key={itemIndex}>{item}</li>
                    ))}
                </ul>
            )}

            {section.subsections && section.subsections.length > 0 && (
                <div className="ml-4">
                    {section.subsections.map((subsection, subIndex) =>
                        renderSection(subsection, subIndex)
                    )}
                </div>
            )}
        </div>
    );
};

const FAQQuickStartCTA: React.FC<{ faq: FAQ; onSendMessage?: (msg: string) => void; assistant?: Assistant | null }> = ({ faq, onSendMessage, assistant }) => {
    const suggestions = faq.quickSuggestions?.slice(0, 3) ?? [];

    const primaryColor = getIconColorFromAssistant(assistant?.bgGradient, assistant?.accentColor, '#6366f1');

    if (suggestions.length === 0) return null;

    return (
        <div className="my-4">
            <p className="text-xs text-gray-400 mb-2">Not sure where to start?</p>
            <div className="grid grid-cols-2 gap-2">
                {suggestions.map((suggestion, i) => (
                    <button
                        key={i}
                        onClick={() => onSendMessage?.(suggestion)}
                        className="flex items-center gap-1.5 px-3 sm:px-4 py-2.5 sm:py-2 rounded-3xl border border-gray-500 bg-[#2d2d2d] text-gray-300 text-xs sm:text-sm hover:border-gray-300 hover:text-white transition-colors w-full min-h-[44px] sm:min-h-0 text-left"
                    >
                        {assistant && (
                            <span className="flex-shrink-0">
                                {renderAssistantIcon({ assistant, iconColor: primaryColor, size: 'sm' })}
                            </span>
                        )}
                        <span className="leading-tight">{suggestion}</span>
                    </button>
                ))}
            </div>
        </div>
    );
};

export const FAQContentRenderer: React.FC<FAQContentRendererProps> = ({ faq, onSendMessage, assistant }) => {
    const [firstSection, ...restSections] = faq.sections;

    return (
        <article className="mb-6">
            <h1 className="text-lg sm:text-2xl md:text-3xl font-bold text-white mb-4 sm:mb-6">
                {faq.h1}
            </h1>

            {/* First section */}
            {firstSection && renderSection(firstSection, 0)}

            {/* CTA + Quick suggestions after first paragraph */}
            <FAQQuickStartCTA faq={faq} onSendMessage={onSendMessage} assistant={assistant} />

            {/* Remaining sections */}
            {restSections.map((section, index) => renderSection(section, index + 1))}

            {/* Related Pages */}
            {faq.relatedPages && faq.relatedPages.length > 0 && (
                <div className="mt-8 pt-6 border-t border-gray-600">
                    <h2 className="text-base sm:text-xl font-bold text-white mb-3 sm:mb-4">Related Pages</h2>
                    <ul className="space-y-2">
                        {faq.relatedPages.map((page, index) => (
                            <li key={index}>
                                <Link
                                    to={page.path}
                                    className="text-cyan-400 hover:text-cyan-300 hover:underline transition-colors"
                                >
                                    {page.title}
                                </Link>
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {/* CTA */}
            {import.meta.env.VITE_ASSISTANT_DISPLAY_NAME === 'DriveSource' && (
                <div className="mt-8 pt-6 border-t border-gray-600">
                    <h2 className="text-base sm:text-xl font-bold text-white mb-2 sm:mb-3">Still unsure what applies to YOUR vehicle?</h2>
                    <p className="text-sm sm:text-base text-gray-300 mb-3">Get a personalized answer based on:</p>
                    <ul className="list-none space-y-1 mb-4">
                        {['your vehicle', 'mileage', 'driving conditions', 'Canadian climate'].map((item) => (
                            <li key={item} className="text-sm sm:text-base text-gray-300 flex items-center gap-2">
                                <span className="text-cyan-400">•</span> {item}
                            </li>
                        ))}
                    </ul>
                    <p className="text-sm sm:text-base text-white font-semibold">Start the conversation below.</p>
                </div>
            )}
        </article>
    );
};
